import { EMPTY_ARRAY } from 'constants/defaults';
import type { AppId } from 'constants/known-apps';
import { ASANA_SYNC, betaApps, CLICKUP, MONDAY, prereleaseApps } from 'constants/known-apps';
import { appIcons } from 'constants/known-apps.icons';
import { DateTime } from 'luxon';
import type { Features } from 'types/account';
import type { App, AppAccount, AppSubscription } from 'types/apps';
import { ScopeTypes } from 'types/apps';
import type { UserTimeFormat } from 'types/user';
import { formatTime } from 'utils/date-helper';

export interface Integration<T extends string = string> {
  appId?: number;
  description: string;
  displayName: string;
  id: T;
  integrated?: boolean;
  logoUrl: string;
  navLink?: string;
  supportUrl?: string | null;
  ui?: {
    isEnabled: boolean;
    isLockedFeature: boolean;
    isPartiallyConnected: boolean;
    isDisconnected: boolean;
    isInvited?: boolean;
    isPrerelease: boolean;
    isBeta: boolean;
    isImporting: boolean;
  };
  url?: string;
}

export interface FactoryApp<O = unknown> extends Required<Omit<Integration<AppId>, 'navLink'>> {
  featureRequirements: Array<keyof Features>;
  integrationsCount: number;
  importedDate: string | null;
  importedTime: string | null;
  importSupport: Array<string> | null;
  createdDate: string | null;
  category: string;
  provider: string;
  scope: ScopeTypes;
  mergedApps: Array<FactoryApp>;
  options?: O;
}

export interface FactoryAppAccount<O = unknown> {
  id: number;
  displayName: string;
  email: string;
  name: string;
  subscriptionsCount: number;
  importedDate: string | null;
  importedTime: string | null;
  exportedDate: string | null;
  exportedTime: string | null;
  createdDate: string;
  isDisconnected: boolean;
  hasSubscriptions: boolean;
  manage: boolean;
  isLoading?: boolean;
  isImporting?: boolean;
  options: O | null;
  adminEmail: string | null;
  isChildConnection: boolean;
  state: 'active' | 'inactive' | 'in_progress' | 'failed';
  resourceId: string | null;
}

export const appFactory = (
  app: App,
  {
    timeFormat,
    canUsePremiumIntegrations,
    canUseAzureADIntegration,
    accountFeatures,
  }: {
    timeFormat: UserTimeFormat;
    canUsePremiumIntegrations?: boolean;
    canUseAzureADIntegration?: boolean;
    accountFeatures: Features;
  }
): FactoryApp => {
  const {
    id,
    app_id: appId,
    display_name: displayName,
    description,
    integrated,
    connected_integrations_count,
    disconnected_integrations_count,
    is_importing,
    last_imported_at,
    first_created_at,
    authorize_url,
    authorize_full_url,
    logo_url,
    scope,
    feature_requirements,
    import_support,
    provider,
  } = app;

  const integrationsCount =
    (connected_integrations_count || 0) + (disconnected_integrations_count || 0);
  const isEnabled = app.integrated && integrationsCount > 0;

  // TODO: This way of special casing invoices is (probably?) obsolete with the new categorization
  // and handling of integrations, but need to check with backend/success before removing this
  const isInvoicesFeature = feature_requirements?.includes('invoices');

  // TODO: Refactor ClickUp integration to work the same way as other sync integrations (using jobs)
  const isImporting = [CLICKUP, MONDAY, ASANA_SYNC].includes(id) && is_importing;

  return {
    url: authorize_full_url || authorize_url,
    appId,
    id,
    displayName,
    logoUrl: appIcons[id] || logo_url,
    description,
    featureRequirements: feature_requirements ?? [],
    importSupport: import_support,
    provider,
    integrated,
    integrationsCount,
    importedDate: last_imported_at
      ? DateTime.fromISO(last_imported_at).toLocaleString(DateTime.DATE_FULL)
      : null,
    importedTime: last_imported_at ? formatTime(last_imported_at, timeFormat) : null,
    createdDate:
      first_created_at && DateTime.fromISO(first_created_at).toLocaleString(DateTime.DATE_FULL),
    category: app.category,
    scope,
    mergedApps: EMPTY_ARRAY,
    ui: {
      isEnabled,
      isLockedFeature:
        ((app.premium || isInvoicesFeature) && !canUsePremiumIntegrations) ||
        (app.unlimited_plus && !canUseAzureADIntegration) ||
        !!feature_requirements?.some((key: keyof Features) => !accountFeatures[key]),
      isPartiallyConnected: isEnabled && app.connected_integrations_count > 0,
      isDisconnected: isEnabled && app.disconnected_integrations_count > 0,
      isPrerelease: prereleaseApps.includes(id),
      isBeta: Object.hasOwn(betaApps, id),
      isImporting,
    },
    supportUrl:
      app.support_url ||
      (id === MONDAY
        ? 'https://support.timelyapp.com/en/articles/8212756-integrate-timely-with-monday-com'
        : null),
  };
};

interface AppAccountFactoryArgs {
  account: AppAccount;
  subscriptions: Array<AppSubscription> | null | undefined;
  userEmail: string;
  timeFormat: UserTimeFormat;
}

export const appAccountFactory = <O = unknown>({
  account,
  subscriptions,
  userEmail,
  timeFormat,
}: AppAccountFactoryArgs): FactoryAppAccount => {
  const {
    id,
    name,
    email,
    first_imported_at,
    last_imported_at,
    last_exported_at,
    created_at,
    objects: hasSubscriptions,
    disconnected: isDisconnected,
    parent: parentConnection,
    manage,
    state,
    resource_id,
  } = account;

  let displayName = userEmail;

  if (name && email) {
    displayName = `${name} (${email})`;
  } else if (name || email) {
    displayName = `${name || email}`;
  }

  let subscriptionsCount = 0;

  if (hasSubscriptions && subscriptions) {
    subscriptionsCount = subscriptions.length;
  }

  const options: O | null = (account.options as O) ?? null;
  const adminEmail = parentConnection?.email || null;
  const isChildConnection = Boolean(parentConnection?.id || false);

  return {
    id,
    displayName,
    name,
    email,
    isDisconnected,
    hasSubscriptions,
    subscriptionsCount,
    manage,
    importedDate: last_imported_at
      ? DateTime.fromISO(last_imported_at).toLocaleString(DateTime.DATE_FULL)
      : null,
    importedTime: last_imported_at ? formatTime(last_imported_at, timeFormat) : null,
    exportedDate: last_exported_at
      ? DateTime.fromISO(last_exported_at).toLocaleString(DateTime.DATE_FULL)
      : null,
    exportedTime: last_exported_at ? formatTime(last_exported_at, timeFormat) : null,
    createdDate: created_at && DateTime.fromISO(created_at).toLocaleString(DateTime.DATE_FULL),
    options,
    isImporting: !!options && !first_imported_at,
    adminEmail: adminEmail,
    isChildConnection: isChildConnection,
    state,
    resourceId: resource_id || null,
  };
};

export const reduceAppByScope = (prev: Record<ScopeTypes, Array<FactoryApp>>, curr: FactoryApp) => {
  if (curr.scope === ScopeTypes.account) {
    prev[ScopeTypes.account].push(curr);
    return prev;
  }
  if (curr.scope === ScopeTypes.user) {
    prev[ScopeTypes.user].push(curr);
    return prev;
  }
  return prev;
};
